import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import * as XLSX from "xlsx";
import Sidebar from "../components/Sidebar";
import { auth, db, storage } from "../firebase";
import { assertPeriodOpen } from "../services/financeCore/periodLockService";

const COMPANY_NAME = "ALM Outsourcing Services Pvt Ltd";
const ACCOUNT_ID = "head_office";
const CLIENT_SITE = "Head Office";
const PETTY_CASH_COUNTER_ID = "global";
const pettyCashHandlerEmails = ["pcash@almhr.com", "norina@almhr.com"];
const financeManagerEmails = ["nazim@almhr.com"];
const pettyCashReportViewerEmails = ["laeeq@almhr.com"];
const paymentModes = ["Cash", "JazzCash", "Bank", "Other"];
const expenseHeadMap = {
  "Stationary item": "Printing and Stationary",
  "General purchase": "Office Supplies",
  "Boss Expense": "Director Expense",
  "Other Miscellaneous": "Other Miscellaneous",
  "Repair": "Repair and Maintenance",
  "Utility and Internet bills": "Utility Bills",
  "Entertainment expense": "Meals and Entertainment",
  "Fuel and Gas": "Fuel & Gas",
  "Late Payment charges": "Late Payment Charges",
  Courier: "Postage and Delivery",
  Travel: "Traveling & Conveyance Expense"
};
const defaultHeads = Object.keys(expenseHeadMap);

function getLocalInputDate(date = new Date()) {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = local.getTimezoneOffset();
  return new Date(local.getTime() - offset * 60 * 1000).toISOString().slice(0, 10);
}

const expenseTemplate = {
  expenseDate: getLocalInputDate(),
  spenderName: "",
  clientProjectSite: CLIENT_SITE,
  description: "",
  expenseHead: "",
  amount: "",
  paymentMode: "Cash",
  remarks: ""
};

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("en-PK", { maximumFractionDigits: 2 });
}

function getCreatorLabel(expense) {
  return expense.enteredByEmail || expense.enteredByName || "Unknown";
}

function formatDateForExcel(value) {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}/${month}/${year}` : value || "";
}

function getCreatedTime(item) {
  if (item.createdAt?.toMillis) return item.createdAt.toMillis();
  if (item.createdAt?.seconds) return item.createdAt.seconds * 1000;
  return 0;
}

function parseInputDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysBetweenDates(fromDate, toDate) {
  const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  return Math.round((end - start) / (24 * 60 * 60 * 1000));
}

function getLegacyCounterKeys(expenseDate) {
  const [yearText] = String(expenseDate || getLocalInputDate()).split("-");
  const year = Number(yearText) || new Date().getFullYear();
  return [year - 1, year].flatMap((counterYear) =>
    Array.from({ length: 12 }, (_, index) => `${counterYear}-${String(index + 1).padStart(2, "0")}`)
  );
}

function numberToWords(value) {
  const amount = Math.floor(Number(value || 0));
  if (!amount) return "Zero Rupees Only";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const scales = [
    { value: 10000000, label: "Crore" },
    { value: 100000, label: "Lakh" },
    { value: 1000, label: "Thousand" },
    { value: 100, label: "Hundred" }
  ];
  const small = (num) => {
    if (num < 10) return ones[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) return `${tens[Math.floor(num / 10)]} ${ones[num % 10]}`.trim();
    return "";
  };
  let remaining = amount;
  const parts = [];
  scales.forEach((scale) => {
    const count = Math.floor(remaining / scale.value);
    if (count) {
      parts.push(`${numberToWords(count).replace(" Rupees Only", "")} ${scale.label}`);
      remaining %= scale.value;
    }
  });
  if (remaining) parts.push(small(remaining));
  return `${parts.join(" ")} Rupees Only`;
}

async function uploadFile(file, folder) {
  if (!file) return null;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${folder}/${Date.now()}-${safeName}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  return { name: file.name, path, url: await getDownloadURL(fileRef) };
}

async function writeAudit(action, details = {}) {
  const user = auth.currentUser;
  try {
    await addDoc(collection(db, "audit_logs"), {
      action,
      details,
      uid: user?.uid || null,
      email: user?.email || "",
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.warn("Audit log skipped:", error.message);
  }
}

function Voucher({ expense }) {
  if (!expense) return null;
  return (
    <section className="voucher-sheet">
      <div className="voucher-top">
        <div className="voucher-brand">
          <img src="/alm-logo.png" alt="ALM logo" />
          <div>
            <h2>{COMPANY_NAME}</h2>
            <p>Petty Cash Voucher</p>
          </div>
        </div>
        <strong>{expense.voucherCode}</strong>
      </div>
      <div className="voucher-grid">
        <span>Date</span><strong>{expense.expenseDate}</strong>
        <span>Spender / Receiver</span><strong>{expense.spenderName}</strong>
        <span>Client / Project / Site</span><strong>{expense.clientProjectSite}</strong>
        <span>Expense Head</span><strong>{expense.expenseHead}</strong>
        <span>QuickBooks Account</span><strong>{expense.quickBooksAccount}</strong>
        <span>Description</span><strong>{expense.description}</strong>
        <span>Amount</span><strong>PKR {formatCurrency(expense.amount)}</strong>
        <span>Amount in Words</span><strong>{numberToWords(expense.amount)}</strong>
        <span>Payment Mode</span><strong>{expense.paymentMode}</strong>
        <span>Remarks</span><strong>{expense.remarks || "N/A"}</strong>
        <span>Receipt Reference</span><strong>{expense.receiptAttachment?.name || "No receipt attached"}</strong>
        <span>Created By</span><strong>{getCreatorLabel(expense)}</strong>
      </div>
      <div className="signature-grid">
        <div><span />Prepared By / Handler</div>
        <div><span />Spender / Receiver</div>
        <div><span />Finance Verification</div>
      </div>
    </section>
  );
}

function SetupList({ title, collectionName, placeholder, fallbackItems }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    try {
      const snapshot = await getDocs(query(collection(db, collectionName), orderBy("name")));
      setItems(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const saveItem = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await addDoc(collection(db, collectionName), {
        name: name.trim(),
        active: true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null
      });
      setName("");
      await loadItems();
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (item) => {
    await updateDoc(doc(db, collectionName, item.id), {
      active: !item.active,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null
    });
    await loadItems();
  };

  const visibleItems = items.length ? items : fallbackItems.map((item) => ({ id: item, name: item, active: true, seed: true }));

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">{title}</h2>
        <span className="badge">{visibleItems.length}</span>
      </div>
      <div className="panel-body">
        <div className="inline-form">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} />
          <button className="btn btn-primary" type="button" disabled={loading} onClick={saveItem}>Add</button>
        </div>
        <div className="setup-list">
          {visibleItems.map((item) => (
            <div className="setup-row" key={item.id}>
              <span>{item.name}</span>
              {item.seed ? <span className="badge">Default</span> : <button className="btn btn-muted" type="button" onClick={() => toggleItem(item)}>{item.active ? "Deactivate" : "Activate"}</button>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function PettyCashPortal() {
  const [tab, setTab] = useState("add");
  const [role, setRole] = useState("handler");
  const [form, setForm] = useState(expenseTemplate);
  const [receiptFile, setReceiptFile] = useState(null);
  const [signedVoucherFile, setSignedVoucherFile] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [account, setAccount] = useState({ openingBalance: 0, currentBalance: 0, totalReceived: 0, totalSpent: 0 });
  const [savedExpense, setSavedExpense] = useState(null);
  const [heads, setHeads] = useState(defaultHeads);
  const [loading, setLoading] = useState(false);
  const [balanceForm, setBalanceForm] = useState({ openingBalance: "", receivedAmount: "", receivedDate: getLocalInputDate(), receivedFrom: "", receiptMode: "Cash", remarks: "", adjustmentAmount: "", adjustmentRemarks: "" });
  const [filters, setFilters] = useState({ search: "", fromDate: "", toDate: "", handler: "", spender: "", client: "", head: "", paymentMode: "", exportStatus: "" });

  const user = auth.currentUser;
  const email = (user?.email || "").toLowerCase();
  const isAdmin = role === "finance";
  const isReportViewer = role === "report";
  const canAddExpense = role === "handler";
  const canUploadSignedVoucher = canAddExpense || isAdmin;
  const enteredBy = user?.displayName || user?.email || "Current User";
  const roleLabel = isAdmin ? "Finance Manager" : isReportViewer ? "Read-only PCASH Report" : "Petty Cash Entry";

  useEffect(() => {
    if (financeManagerEmails.includes(email)) {
      setRole("finance");
      setTab("admin");
    } else if (pettyCashReportViewerEmails.includes(email)) {
      setRole("report");
      setTab("report");
    } else {
      setRole("handler");
      setTab("add");
    }
  }, [email]);

  const loadSetup = async () => {
    try {
      const headsSnapshot = await getDocs(query(collection(db, "expense_heads"), where("active", "==", true)));
      if (!headsSnapshot.empty) setHeads(headsSnapshot.docs.map((item) => item.data().name).sort());
    } catch {
      setHeads(defaultHeads);
    }
  };

  const loadBalance = async () => {
    try {
      const accountSnapshot = await getDocs(query(collection(db, "petty_cash_accounts"), where("accountId", "==", ACCOUNT_ID)));
      if (!accountSnapshot.empty) setAccount({ id: accountSnapshot.docs[0].id, ...accountSnapshot.docs[0].data() });
      if (role === "report") {
        setLedger([]);
        return;
      }
      const ledgerSnapshot = await getDocs(query(collection(db, "petty_cash_ledger"), where("accountId", "==", ACCOUNT_ID)));
      setLedger(ledgerSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => getCreatedTime(b) - getCreatedTime(a)).slice(0, 200));
    } catch (error) {
      console.warn("Balance could not be loaded:", error.message);
    }
  };

  const loadExpenses = async () => {
    if (!user) return;
    try {
      const expensesQuery = isAdmin
        ? query(collection(db, "petty_cash_expenses"))
        : isReportViewer
          ? query(collection(db, "petty_cash_expenses"), where("enteredByEmail", "in", pettyCashHandlerEmails))
          : query(collection(db, "petty_cash_expenses"), where("enteredByUid", "==", user.uid));
      const snapshot = await getDocs(expensesQuery);
      setExpenses(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => getCreatedTime(b) - getCreatedTime(a)).slice(0, 200));
    } catch (error) {
      alert(`Petty cash expenses could not be loaded: ${error.message}`);
      setExpenses([]);
    }
  };

  useEffect(() => {
    loadSetup();
  }, []);

  useEffect(() => {
    loadExpenses();
    loadBalance();
  }, [role]);

  const totals = useMemo(() => {
    const total = expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const exported = expenses.filter((item) => item.exportedToQuickBooks).length;
    return { total, exported, open: expenses.length - exported };
  }, [expenses]);

  const filteredExpenses = expenses.filter((expense) => {
    const search = filters.search.trim().toLowerCase();
    const text = [expense.voucherCode, expense.expenseDate, expense.spenderName, expense.clientProjectSite, expense.expenseHead, expense.quickBooksAccount, expense.description, expense.amount, expense.paymentMode, expense.remarks, expense.enteredByName].filter(Boolean).join(" ").toLowerCase();
    return (!search || text.includes(search))
      && (!filters.spender || expense.spenderName?.toLowerCase().includes(filters.spender.toLowerCase()))
      && (!filters.handler || expense.enteredByName?.toLowerCase().includes(filters.handler.toLowerCase()))
      && (!filters.client || expense.clientProjectSite === filters.client)
      && (!filters.head || expense.expenseHead === filters.head)
      && (!filters.paymentMode || expense.paymentMode === filters.paymentMode)
      && (!filters.exportStatus || (filters.exportStatus === "exported" ? expense.exportedToQuickBooks : !expense.exportedToQuickBooks))
      && (!filters.fromDate || expense.expenseDate >= filters.fromDate)
      && (!filters.toDate || expense.expenseDate <= filters.toDate);
  });

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateBalanceForm = (field, value) => setBalanceForm((current) => ({ ...current, [field]: value }));

  const saveExpense = async () => {
    if (!canAddExpense) {
      alert("This account can view and export petty cash records, but cannot create vouchers.");
      return;
    }
    const required = ["expenseDate", "spenderName", "clientProjectSite", "description", "expenseHead", "amount", "paymentMode"];
    if (required.some((field) => !String(form[field] || "").trim())) {
      alert("Please complete all required expense fields.");
      return;
    }
    const amount = Number(form.amount || 0);
    if (amount <= 0) {
      alert("Amount must be greater than zero.");
      return;
    }
    const expenseDate = parseInputDate(form.expenseDate);
    const today = parseInputDate(getLocalInputDate());
    if (!expenseDate || !today) {
      alert("Please enter a valid expense date.");
      return;
    }
    const expenseAgeDays = daysBetweenDates(expenseDate, today);
    if (expenseAgeDays > 10) {
      alert("Voucher cannot be generated for an expense date more than 10 days old.");
      return;
    }
    if (Number(account.currentBalance || 0) < amount) {
      alert("Insufficient petty cash balance. Please record cash received before saving this voucher.");
      return;
    }

    try {
      await assertPeriodOpen(form.expenseDate);
    } catch (lockError) {
      alert(lockError.message);
      return;
    }

    setLoading(true);
    try {
      const result = await runTransaction(db, async (transaction) => {
        const [, monthValue] = String(form.expenseDate).split("-");
        const legacyCounterKeys = getLegacyCounterKeys(form.expenseDate);
        const counterRef = doc(db, "voucher_counters", PETTY_CASH_COUNTER_ID);
        const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
        const expenseRef = doc(collection(db, "petty_cash_expenses"));
        const ledgerRef = doc(collection(db, "petty_cash_ledger"));
        const counterSnapshot = await transaction.get(counterRef);
        const legacyCounterSnapshots = await Promise.all(legacyCounterKeys.map((key) => transaction.get(doc(db, "voucher_counters", key))));
        const accountSnapshot = await transaction.get(accountRef);
        const currentBalance = accountSnapshot.exists() ? Number(accountSnapshot.data().currentBalance || 0) : 0;
        if (currentBalance < amount) throw new Error("Insufficient petty cash balance.");
        const highestLegacySequence = legacyCounterSnapshots.reduce((highest, snapshot) => (
          snapshot.exists() ? Math.max(highest, Number(snapshot.data().lastSequence || 0)) : highest
        ), 0);
        const globalSequence = counterSnapshot.exists() ? Number(counterSnapshot.data().lastSequence || 0) : 0;
        const nextSequence = Math.max(globalSequence, highestLegacySequence) + 1;
        const voucherCode = `PCV-${monthValue}-${String(nextSequence).padStart(4, "0")}`;
        const nextBalance = currentBalance - amount;
        const payload = {
          ...form,
          amount,
          voucherCode,
          quickBooksAccount: expenseHeadMap[form.expenseHead] || form.expenseHead,
          entryDate: getLocalInputDate(),
          receiptAttachment: null,
          signedVoucherAttachment: null,
          enteredByUid: user?.uid || null,
          enteredByName: enteredBy,
          enteredByEmail: email,
          exportedToQuickBooks: false,
          locked: false,
          accountId: ACCOUNT_ID,
          balanceAfter: nextBalance,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        transaction.set(counterRef, { counterId: PETTY_CASH_COUNTER_ID, lastSequence: nextSequence, lastVoucherCode: voucherCode, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }, { merge: true });
        transaction.set(expenseRef, payload);
        transaction.set(ledgerRef, { accountId: ACCOUNT_ID, type: "expense", voucherCode, amount: -amount, balanceAfter: nextBalance, description: form.description, createdBy: user?.uid || null, createdByName: enteredBy, createdAt: serverTimestamp() });
        transaction.set(accountRef, { accountId: ACCOUNT_ID, currentBalance: nextBalance, totalSpent: (accountSnapshot.exists() ? Number(accountSnapshot.data().totalSpent || 0) : 0) + amount, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }, { merge: true });
        return { id: expenseRef.id, voucherCode, payload };
      });
      let receiptAttachment = null;
      if (receiptFile) {
        receiptAttachment = await uploadFile(receiptFile, `petty-cash/${result.voucherCode}/receipts`);
        await updateDoc(doc(db, "petty_cash_expenses", result.id), { receiptAttachment, updatedAt: serverTimestamp() });
      }
      const finalExpense = { id: result.id, ...result.payload, receiptAttachment };
      await writeAudit("petty_cash_expenses.create", { id: result.id, voucherCode: result.voucherCode });
      setSavedExpense(finalExpense);
      setExpenses((current) => [finalExpense, ...current].slice(0, 200));
      setForm(expenseTemplate);
      setReceiptFile(null);
      setTab("voucher");
      await loadBalance();
    } catch (error) {
      alert(error.message || "Expense could not be saved.");
    } finally {
      setLoading(false);
    }
  };

  const setOpeningBalance = async () => {
    if (!isAdmin) return;
    const amount = Number(balanceForm.openingBalance || 0);
    if (amount < 0) {
      alert("Opening balance cannot be negative.");
      return;
    }
    await runTransaction(db, async (transaction) => {
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(collection(db, "petty_cash_ledger"));
      const accountSnapshot = await transaction.get(accountRef);
      const previousOpening = accountSnapshot.exists() ? Number(accountSnapshot.data().openingBalance || 0) : 0;
      const previousCurrent = accountSnapshot.exists() ? Number(accountSnapshot.data().currentBalance || 0) : 0;
      const nextBalance = previousCurrent - previousOpening + amount;
      transaction.set(accountRef, { accountId: ACCOUNT_ID, openingBalance: amount, currentBalance: nextBalance, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }, { merge: true });
      transaction.set(ledgerRef, { accountId: ACCOUNT_ID, type: "opening", amount, balanceAfter: nextBalance, description: "Opening balance set", createdBy: user?.uid || null, createdByName: enteredBy, createdAt: serverTimestamp() });
    });
    setBalanceForm((current) => ({ ...current, openingBalance: "" }));
    await loadBalance();
  };

  const addCashReceived = async () => {
    const amount = Number(balanceForm.receivedAmount || 0);
    if (amount <= 0) {
      alert("Received amount must be greater than zero.");
      return;
    }
    await runTransaction(db, async (transaction) => {
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(collection(db, "petty_cash_ledger"));
      const accountSnapshot = await transaction.get(accountRef);
      const currentBalance = accountSnapshot.exists() ? Number(accountSnapshot.data().currentBalance || 0) : 0;
      const totalReceived = accountSnapshot.exists() ? Number(accountSnapshot.data().totalReceived || 0) : 0;
      const nextBalance = currentBalance + amount;
      transaction.set(accountRef, { accountId: ACCOUNT_ID, currentBalance: nextBalance, totalReceived: totalReceived + amount, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }, { merge: true });
      transaction.set(ledgerRef, { accountId: ACCOUNT_ID, type: "receipt", amount, balanceAfter: nextBalance, receivedDate: balanceForm.receivedDate, receivedFrom: balanceForm.receivedFrom, receiptMode: balanceForm.receiptMode, remarks: balanceForm.remarks, description: `Cash received${balanceForm.receivedFrom ? ` from ${balanceForm.receivedFrom}` : ""}`, createdBy: user?.uid || null, createdByName: enteredBy, createdAt: serverTimestamp() });
    });
    setBalanceForm((current) => ({ ...current, receivedAmount: "", receivedFrom: "", remarks: "" }));
    await loadBalance();
  };

  const addBalanceAdjustment = async () => {
    if (!isAdmin) return;
    const amount = Number(balanceForm.adjustmentAmount || 0);
    if (!amount) {
      alert("Enter an adjustment amount. Use minus for deduction and plus for credit.");
      return;
    }
    await runTransaction(db, async (transaction) => {
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(collection(db, "petty_cash_ledger"));
      const accountSnapshot = await transaction.get(accountRef);
      const currentBalance = accountSnapshot.exists() ? Number(accountSnapshot.data().currentBalance || 0) : 0;
      const nextBalance = currentBalance + amount;
      transaction.set(accountRef, { accountId: ACCOUNT_ID, currentBalance: nextBalance, updatedAt: serverTimestamp(), updatedBy: user?.uid || null }, { merge: true });
      transaction.set(ledgerRef, {
        accountId: ACCOUNT_ID,
        type: "adjustment",
        amount,
        balanceAfter: nextBalance,
        description: balanceForm.adjustmentRemarks || "Finance balance correction",
        remarks: balanceForm.adjustmentRemarks,
        createdBy: user?.uid || null,
        createdByName: enteredBy,
        createdAt: serverTimestamp()
      });
    });
    await writeAudit("petty_cash_balance.adjust", { amount, remarks: balanceForm.adjustmentRemarks });
    setBalanceForm((current) => ({ ...current, adjustmentAmount: "", adjustmentRemarks: "" }));
    await loadBalance();
  };

  const editLedgerEntry = async (row) => {
    if (!isAdmin || row.type === "expense") return;
    const oldAmount = Number(row.amount || 0);
    const amountInput = window.prompt("Correct amount. Use minus for deduction and plus for credit.", String(oldAmount));
    if (amountInput === null) return;
    const newAmount = Number(amountInput);
    if (!Number.isFinite(newAmount)) {
      alert("Please enter a valid amount.");
      return;
    }
    const description = window.prompt("Description / remarks", row.description || row.remarks || "");
    await runTransaction(db, async (transaction) => {
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(db, "petty_cash_ledger", row.id);
      const accountSnapshot = await transaction.get(accountRef);
      const ledgerSnapshot = await transaction.get(ledgerRef);
      if (!ledgerSnapshot.exists()) throw new Error("This balance entry no longer exists.");
      const accountData = accountSnapshot.exists() ? accountSnapshot.data() : {};
      const ledgerData = ledgerSnapshot.data();
      const previousAmount = Number(ledgerData.amount || 0);
      const delta = newAmount - previousAmount;
      const nextBalance = Number(accountData.currentBalance || 0) + delta;
      const accountUpdate = { accountId: ACCOUNT_ID, currentBalance: nextBalance, updatedAt: serverTimestamp(), updatedBy: user?.uid || null };
      if (ledgerData.type === "receipt") accountUpdate.totalReceived = Math.max(0, Number(accountData.totalReceived || 0) + delta);
      if (ledgerData.type === "opening") accountUpdate.openingBalance = Math.max(0, Number(accountData.openingBalance || 0) + delta);
      transaction.set(accountRef, accountUpdate, { merge: true });
      transaction.update(ledgerRef, {
        amount: newAmount,
        balanceAfter: nextBalance,
        description: description || ledgerData.description || "Finance balance correction",
        remarks: description || ledgerData.remarks || "",
        correctedAt: serverTimestamp(),
        correctedBy: user?.uid || null,
        correctedByName: enteredBy
      });
    });
    await writeAudit("petty_cash_ledger.edit", { id: row.id, oldAmount, newAmount });
    await loadBalance();
  };

  const removeLedgerEntry = async (row) => {
    if (!isAdmin || row.type === "expense") return;
    if (!window.confirm("Remove this balance entry and reverse its effect on current balance?")) return;
    await runTransaction(db, async (transaction) => {
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(db, "petty_cash_ledger", row.id);
      const accountSnapshot = await transaction.get(accountRef);
      const ledgerSnapshot = await transaction.get(ledgerRef);
      if (!ledgerSnapshot.exists()) throw new Error("This balance entry no longer exists.");
      const accountData = accountSnapshot.exists() ? accountSnapshot.data() : {};
      const ledgerData = ledgerSnapshot.data();
      const amount = Number(ledgerData.amount || 0);
      const nextBalance = Number(accountData.currentBalance || 0) - amount;
      const accountUpdate = { accountId: ACCOUNT_ID, currentBalance: nextBalance, updatedAt: serverTimestamp(), updatedBy: user?.uid || null };
      if (ledgerData.type === "receipt") accountUpdate.totalReceived = Math.max(0, Number(accountData.totalReceived || 0) - amount);
      if (ledgerData.type === "opening") accountUpdate.openingBalance = Math.max(0, Number(accountData.openingBalance || 0) - amount);
      transaction.set(accountRef, accountUpdate, { merge: true });
      transaction.delete(ledgerRef);
    });
    await writeAudit("petty_cash_ledger.delete", { id: row.id, type: row.type, amount: row.amount });
    await loadBalance();
  };

  const attachSignedVoucher = async (expense) => {
    if (!signedVoucherFile) {
      alert("Please choose a signed voucher image first.");
      return;
    }
    const attachment = await uploadFile(signedVoucherFile, `petty-cash/${expense.voucherCode}/signed-vouchers`);
    await updateDoc(doc(db, "petty_cash_expenses", expense.id), { signedVoucherAttachment: attachment, updatedAt: serverTimestamp(), updatedBy: user?.uid || null });
    setSignedVoucherFile(null);
    await loadExpenses();
  };

  const exportToExcel = async () => {
    if (!isAdmin) {
      alert("Only finance users can export petty cash records.");
      return;
    }
    const rows = filteredExpenses.map((expense) => ({
      Date: formatDateForExcel(expense.expenseDate),
      "Voucher Code": expense.voucherCode,
      "Payee / Spender Name": expense.spenderName,
      "Expense Header": expense.expenseHead,
      "Account / Expense Head": expense.quickBooksAccount,
      Description: expense.description,
      Amount: Number(expense.amount || 0),
      "Client / Project / Site": expense.clientProjectSite,
      "Payment Account": expense.paymentMode,
      "Memo / Remarks": expense.remarks || "",
      "Entered By": expense.enteredByName || ""
    }));
    if (!rows.length) {
      alert("No expenses match the current filters.");
      return;
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "QuickBooks Export");
    const batchRef = await addDoc(collection(db, "export_batches"), { createdAt: serverTimestamp(), createdBy: user?.uid || null, rowCount: rows.length, voucherCodes: rows.map((row) => row["Voucher Code"]) });
    await Promise.all(filteredExpenses.map((expense) => updateDoc(doc(db, "petty_cash_expenses", expense.id), { exportedToQuickBooks: true, exportBatchId: batchRef.id, locked: true, updatedAt: serverTimestamp(), updatedBy: user?.uid || null })));
    XLSX.writeFile(workbook, `quickbooks-petty-cash-${new Date().toISOString().slice(0, 10)}.xlsx`);
    await loadExpenses();
  };

  const unlockExpense = async (expense) => {
    await updateDoc(doc(db, "petty_cash_expenses", expense.id), { locked: false, updatedAt: serverTimestamp(), unlockedAt: serverTimestamp(), unlockedBy: user?.uid || null });
    await loadExpenses();
  };

  const editExpenseHead = async (expense) => {
    if (!isAdmin) return;
    const headList = heads.length ? heads : defaultHeads;
    const selected = window.prompt(`Enter correct expense head:\n\n${headList.join("\n")}`, expense.expenseHead || "");
    if (selected === null) return;
    const nextHead = selected.trim();
    if (!nextHead) {
      alert("Expense head cannot be blank.");
      return;
    }
    if (!headList.includes(nextHead)) {
      alert("Please enter one of the available expense heads exactly as shown.");
      return;
    }

    const nextAccount = expenseHeadMap[nextHead] || nextHead;
    const payload = {
      expenseHead: nextHead,
      quickBooksAccount: nextAccount,
      updatedAt: serverTimestamp(),
      updatedBy: user?.uid || null,
      headCorrectedAt: serverTimestamp(),
      headCorrectedBy: user?.uid || null
    };

    await updateDoc(doc(db, "petty_cash_expenses", expense.id), payload);
    await writeAudit("petty_cash_expenses.edit_head", {
      id: expense.id,
      voucherCode: expense.voucherCode,
      from: expense.expenseHead,
      to: nextHead,
      quickBooksAccount: nextAccount
    });
    if (savedExpense?.id === expense.id) {
      setSavedExpense((current) => current ? { ...current, expenseHead: nextHead, quickBooksAccount: nextAccount } : current);
    }
    await loadExpenses();
  };

  const deleteExpense = async (expense) => {
    if (!isAdmin) return;
    if (!window.confirm(`Remove voucher ${expense.voucherCode}? Its amount will be credited back, but the voucher number will not be reused.`)) return;
    try {
      await assertPeriodOpen(expense.expenseDate);
    } catch (lockError) {
      alert(lockError.message);
      return;
    }
    await runTransaction(db, async (transaction) => {
      const expenseRef = doc(db, "petty_cash_expenses", expense.id);
      const accountRef = doc(db, "petty_cash_accounts", ACCOUNT_ID);
      const ledgerRef = doc(collection(db, "petty_cash_ledger"));
      const accountSnapshot = await transaction.get(accountRef);
      const accountData = accountSnapshot.exists() ? accountSnapshot.data() : {};
      const amount = Number(expense.amount || 0);
      const nextBalance = Number(accountData.currentBalance || 0) + amount;
      transaction.delete(expenseRef);
      transaction.set(accountRef, {
        accountId: ACCOUNT_ID,
        currentBalance: nextBalance,
        totalSpent: Math.max(0, Number(accountData.totalSpent || 0) - amount),
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null
      }, { merge: true });
      transaction.set(ledgerRef, {
        accountId: ACCOUNT_ID,
        type: "expense_reversal",
        voucherCode: expense.voucherCode,
        amount,
        balanceAfter: nextBalance,
        description: `Voucher ${expense.voucherCode} deleted / expense reversed`,
        createdBy: user?.uid || null,
        createdByName: enteredBy,
        createdAt: serverTimestamp()
      });
    });
    await writeAudit("petty_cash_expenses.delete", { id: expense.id, voucherCode: expense.voucherCode });
    if (savedExpense?.id === expense.id) setSavedExpense(null);
    await loadExpenses();
    await loadBalance();
  };

  const renderSearchBar = (placeholder) => (
    <div className="panel-body search-panel">
      <input className="input" placeholder={placeholder} value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
      {filters.search && <button type="button" className="btn btn-muted" onClick={() => setFilters({ ...filters, search: "" })}>Clear</button>}
    </div>
  );

  const renderExpenseTable = (rows) => (
    <div className="table-wrap petty-table-wrap">
      <table className="data-table">
        <thead>
          <tr><th>Voucher</th><th>Date</th><th>Spender</th><th>Head Office</th><th>Head</th><th>Mode</th><th>Amount</th><th>Created By</th><th>Status</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {rows.map((expense) => (
            <tr key={expense.id}>
              <td>{expense.voucherCode}</td><td>{expense.expenseDate}</td><td>{expense.spenderName}</td><td>{expense.clientProjectSite}</td><td>{expense.expenseHead}</td><td>{expense.paymentMode}</td><td>PKR {formatCurrency(expense.amount)}</td><td>{getCreatorLabel(expense)}</td><td><span className="badge">{expense.exportedToQuickBooks ? "Exported" : "Open"}</span></td>
              <td><div className="table-actions">
                <button className="btn btn-muted" type="button" onClick={() => { setSavedExpense(expense); setTab("voucher"); }}>Voucher</button>
                {expense.receiptAttachment?.url && <a className="btn btn-muted" href={expense.receiptAttachment.url} target="_blank" rel="noreferrer">Receipt</a>}
                {expense.signedVoucherAttachment?.url && <a className="btn btn-muted" href={expense.signedVoucherAttachment.url} target="_blank" rel="noreferrer">Signed</a>}
                {isAdmin && <button className="btn btn-muted" type="button" onClick={() => editExpenseHead(expense)}>Edit Head</button>}
                {isAdmin && expense.locked && <button className="btn btn-muted" type="button" onClick={() => unlockExpense(expense)}>Unlock</button>}
                {isAdmin && <button className="btn btn-danger" type="button" onClick={() => deleteExpense(expense)}>Delete</button>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="app-shell petty-cash-page">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Petty Cash Operations</div>
            <h1 className="page-title">Petty Cash Portal</h1>
            <p className="page-copy">Record petty cash expenses, print vouchers, export QuickBooks Excel files, and tally cash balance.</p>
          </div>
          <div className="role-card"><span>Signed in as</span><strong>{enteredBy}</strong><em>{roleLabel}</em></div>
        </header>

        <section className="card-grid petty-metrics">
          <div className="metric-card"><div className="metric-label">Current Balance</div><div className="metric-value">PKR {formatCurrency(account.currentBalance)}</div></div>
          <div className="metric-card"><div className="metric-label">Visible Expenses</div><div className="metric-value">{expenses.length}</div></div>
          <div className="metric-card"><div className="metric-label">Total Spent</div><div className="metric-value">PKR {formatCurrency(totals.total)}</div></div>
        </section>

        <div className="tabs">
          {canAddExpense && <button className={tab === "add" ? "active" : ""} onClick={() => setTab("add")} type="button">Add Expense</button>}
          {canAddExpense && <button className={tab === "mine" ? "active" : ""} onClick={() => setTab("mine")} type="button">My Expenses</button>}
          {isReportViewer && <button className={tab === "report" ? "active" : ""} onClick={() => setTab("report")} type="button">PCASH Report</button>}
          <button className={tab === "voucher" ? "active" : ""} onClick={() => setTab("voucher")} type="button">Voucher</button>
          {!isReportViewer && <button className={tab === "balance" ? "active" : ""} onClick={() => setTab("balance")} type="button">Balance</button>}
          {isAdmin && <button className={tab === "admin" ? "active" : ""} onClick={() => setTab("admin")} type="button">Admin</button>}
          {isAdmin && <button className={tab === "setup" ? "active" : ""} onClick={() => setTab("setup")} type="button">Setup</button>}
          {isAdmin && <button className={tab === "export" ? "active" : ""} onClick={() => setTab("export")} type="button">Export</button>}
        </div>

        {tab === "add" && canAddExpense && (
          <section className="panel voucher-entry-panel">
            <div className="panel-header"><div><h2 className="panel-title">Voucher Entry</h2><p className="page-copy" style={{ marginTop: 4 }}>Voucher code is generated after save. Expense date cannot be more than 10 days old.</p></div><span className="badge">PCV-MM-####</span></div>
            <div className="panel-body">
              <div className="form-grid voucher-entry-grid">
                <div className="field"><label>Expense Date *</label><input className="input" type="date" value={form.expenseDate} onChange={(e) => updateForm("expenseDate", e.target.value)} /></div>
                <div className="field"><label>Entry Date</label><input className="input" value={getLocalInputDate()} disabled /></div>
                <div className="field"><label>Spender / Receiver Name *</label><input className="input" value={form.spenderName} onChange={(e) => updateForm("spenderName", e.target.value)} /></div>
                <div className="field"><label>Client / Project / Site</label><input className="input" value={CLIENT_SITE} disabled /></div>
                <div className="field"><label>Expense Header *</label><select className="input" value={form.expenseHead} onChange={(e) => updateForm("expenseHead", e.target.value)}><option value="">Select</option>{heads.map((item) => <option key={item}>{item}</option>)}</select></div>
                <div className="field"><label>Output Account</label><input className="input" value={expenseHeadMap[form.expenseHead] || ""} disabled /></div>
                <div className="field"><label>Amount *</label><input className="input" type="number" min="1" value={form.amount} onChange={(e) => updateForm("amount", e.target.value)} /></div>
                <div className="field"><label>Payment Mode *</label><select className="input" value={form.paymentMode} onChange={(e) => updateForm("paymentMode", e.target.value)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></div>
                <div className="field"><label>Entered By</label><input className="input" value={enteredBy} disabled /></div>
                <div className="field field-wide"><label>Expense Description *</label><textarea className="textarea" value={form.description} onChange={(e) => updateForm("description", e.target.value)} /></div>
                <div className="field field-wide"><label>Remarks</label><textarea className="textarea" value={form.remarks} onChange={(e) => updateForm("remarks", e.target.value)} /></div>
                <div className="field"><label>Receipt Attachment</label><input className="input" type="file" accept="image/*,.pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} /></div>
              </div>
              <div className="button-row"><button className="btn btn-primary" type="button" onClick={saveExpense} disabled={loading}>{loading ? "Saving..." : "Save & Generate Voucher"}</button></div>
            </div>
          </section>
        )}

        {tab === "mine" && <section className="panel"><div className="panel-header"><h2 className="panel-title">My Entered Expenses</h2><span className="badge">{filteredExpenses.length} rows</span></div>{renderSearchBar("Search by voucher, spender, header, amount, or remarks")}{filteredExpenses.length ? renderExpenseTable(filteredExpenses) : <div className="empty-state"><h3 style={{ margin: 0 }}>No expenses yet</h3></div>}</section>}

        {tab === "report" && isReportViewer && <section className="panel"><div className="panel-header"><div><h2 className="panel-title">PCASH Read-only Report</h2><p className="page-copy" style={{ marginTop: 4 }}>Entries created by pcash@almhr.com. Use date range or search to check a voucher.</p></div><span className="badge">{filteredExpenses.length} rows</span></div><div className="panel-body filter-grid"><input className="input" placeholder="Search voucher, spender, description, amount" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} /><input className="input" type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} /><input className="input" type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} /><input className="input" placeholder="Spender / Receiver" value={filters.spender} onChange={(e) => setFilters({ ...filters, spender: e.target.value })} /><select className="input" value={filters.head} onChange={(e) => setFilters({ ...filters, head: e.target.value })}><option value="">All heads</option>{heads.map((item) => <option key={item}>{item}</option>)}</select><select className="input" value={filters.paymentMode} onChange={(e) => setFilters({ ...filters, paymentMode: e.target.value })}><option value="">All modes</option>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></div>{filteredExpenses.length ? renderExpenseTable(filteredExpenses) : <div className="empty-state"><h3 style={{ margin: 0 }}>No PCASH entries found</h3></div>}</section>}

        {tab === "voucher" && (
          <div>
            {savedExpense ? (
              <>
                <div className="button-row voucher-actions">{canUploadSignedVoucher && <input className="input" type="file" accept="image/*,.pdf" onChange={(e) => setSignedVoucherFile(e.target.files?.[0] || null)} />}{isAdmin && <button className="btn btn-muted" onClick={() => editExpenseHead(savedExpense)} type="button">Edit Expense Head</button>}{canUploadSignedVoucher && <button className="btn btn-muted" disabled={loading} onClick={() => attachSignedVoucher(savedExpense)} type="button">Upload Signed Voucher</button>}<button className="btn btn-primary" onClick={() => window.print()} type="button">Print / Download</button></div>
                <div className="voucher-print-page" id="petty-cash-voucher"><div className="voucher-copy"><Voucher expense={savedExpense} /></div><div className="voucher-copy print-blank-space" aria-hidden="true" /></div>
              </>
            ) : <div className="empty-state"><h3 style={{ margin: 0 }}>No voucher selected</h3><p className="page-copy" style={{ margin: "8px auto 0" }}>Save an expense or open a voucher from an expense list.</p></div>}
          </div>
        )}

        {tab === "balance" && (
          <div className="balance-grid">
            <section className="panel">
              <div className="panel-header"><h2 className="panel-title">Opening Balance and Cash Received</h2><span className="badge">PKR {formatCurrency(account.currentBalance)}</span></div>
              <div className="panel-body">
                {isAdmin && <div className="form-grid balance-form"><div className="field"><label>Opening Balance</label><input className="input" type="number" value={balanceForm.openingBalance} onChange={(e) => updateBalanceForm("openingBalance", e.target.value)} /></div><div className="field form-action-field"><button className="btn btn-primary" type="button" onClick={setOpeningBalance}>Set Opening Balance</button></div></div>}
                <div className="form-grid balance-form" style={{ marginTop: isAdmin ? 18 : 0 }}>
                  <div className="field"><label>Received Date</label><input className="input" type="date" value={balanceForm.receivedDate} onChange={(e) => updateBalanceForm("receivedDate", e.target.value)} /></div>
                  <div className="field"><label>Amount Received</label><input className="input" type="number" value={balanceForm.receivedAmount} onChange={(e) => updateBalanceForm("receivedAmount", e.target.value)} /></div>
                  <div className="field"><label>Received From</label><input className="input" value={balanceForm.receivedFrom} onChange={(e) => updateBalanceForm("receivedFrom", e.target.value)} /></div>
                  <div className="field"><label>Mode</label><select className="input" value={balanceForm.receiptMode} onChange={(e) => updateBalanceForm("receiptMode", e.target.value)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></div>
                  <div className="field field-wide"><label>Remarks</label><textarea className="textarea" value={balanceForm.remarks} onChange={(e) => updateBalanceForm("remarks", e.target.value)} /></div>
                </div>
                <div className="button-row"><button className="btn btn-success" type="button" onClick={addCashReceived}>Add Cash Received</button></div>
                {isAdmin && (
                  <div className="form-grid balance-form correction-form">
                    <div className="field">
                      <label>Finance Adjustment</label>
                      <input className="input" type="number" value={balanceForm.adjustmentAmount} onChange={(e) => updateBalanceForm("adjustmentAmount", e.target.value)} placeholder="Use - for deduction" />
                    </div>
                    <div className="field field-wide">
                      <label>Correction Remarks</label>
                      <input className="input" value={balanceForm.adjustmentRemarks} onChange={(e) => updateBalanceForm("adjustmentRemarks", e.target.value)} placeholder="Reason for balance correction" />
                    </div>
                    <div className="field form-action-field">
                      <button className="btn btn-muted" type="button" onClick={addBalanceAdjustment}>Add Adjustment</button>
                    </div>
                  </div>
                )}
              </div>
            </section>
            <section className="panel">
              <div className="panel-header"><h2 className="panel-title">Balance Ledger</h2><span className="badge">{ledger.length} rows</span></div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Type</th><th>Description</th><th>Amount</th><th>Balance After</th><th>By</th>{isAdmin && <th>Actions</th>}</tr>
                  </thead>
                  <tbody>
                    {ledger.map((row) => (
                      <tr key={row.id}>
                        <td>{row.type}</td>
                        <td>{row.description || row.voucherCode}</td>
                        <td>PKR {formatCurrency(row.amount)}</td>
                        <td>PKR {formatCurrency(row.balanceAfter)}</td>
                        <td>{row.createdByName || ""}</td>
                        {isAdmin && (
                          <td>
                            {row.type === "expense" ? (
                              <span className="badge">Delete voucher</span>
                            ) : (
                              <div className="table-actions">
                                <button className="btn btn-muted" type="button" onClick={() => editLedgerEntry(row)}>Edit</button>
                                <button className="btn btn-danger" type="button" onClick={() => removeLedgerEntry(row)}>Remove</button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}

        {tab === "admin" && isAdmin && <section className="panel"><div className="panel-header"><h2 className="panel-title">Finance/Admin Dashboard</h2><span className="badge">{filteredExpenses.length} filtered</span></div><div className="panel-body filter-grid"><input className="input" placeholder="Search voucher, spender, description, amount" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} /><input className="input" type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} /><input className="input" type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} /><input className="input" placeholder="Handler" value={filters.handler} onChange={(e) => setFilters({ ...filters, handler: e.target.value })} /><input className="input" placeholder="Spender / Receiver" value={filters.spender} onChange={(e) => setFilters({ ...filters, spender: e.target.value })} /><select className="input" value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })}><option value="">All clients/sites</option><option value={CLIENT_SITE}>{CLIENT_SITE}</option></select><select className="input" value={filters.head} onChange={(e) => setFilters({ ...filters, head: e.target.value })}><option value="">All heads</option>{heads.map((item) => <option key={item}>{item}</option>)}</select><select className="input" value={filters.paymentMode} onChange={(e) => setFilters({ ...filters, paymentMode: e.target.value })}><option value="">All modes</option>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select><select className="input" value={filters.exportStatus} onChange={(e) => setFilters({ ...filters, exportStatus: e.target.value })}><option value="">All statuses</option><option value="open">Open</option><option value="exported">Exported</option></select></div>{filteredExpenses.length ? renderExpenseTable(filteredExpenses) : <div className="empty-state"><h3 style={{ margin: 0 }}>No matching expenses</h3></div>}</section>}

        {tab === "setup" && isAdmin && <div className="setup-grid"><SetupList title="Expense Heads Setup" collectionName="expense_heads" placeholder="Add expense head" fallbackItems={defaultHeads} /></div>}

        {tab === "export" && isAdmin && <section className="panel"><div className="panel-header"><div><h2 className="panel-title">QuickBooks Excel Export</h2><p className="page-copy" style={{ marginTop: 4 }}>Exports current filtered rows and locks them as exported.</p></div><button className="btn btn-success" type="button" onClick={exportToExcel}>Export to Excel</button></div>{renderSearchBar("Search voucher before export")}{filteredExpenses.length ? renderExpenseTable(filteredExpenses) : <div className="empty-state"><h3 style={{ margin: 0 }}>No expenses ready for export</h3></div>}</section>}
      </main>
    </div>
  );
}
